import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/utils/api'

export interface PointTransaction {
  id: string
  user_id?: string
  points: number
  transaction_type: string
  description: string
  created_at: string
  user_name?: string
  user_color?: string
}

export interface LeaderboardEntry {
  id: string
  username: string
  display_name: string
  color_theme: string
  points: number
  total_points: number
  weekly_points: number
  level: number
  streak_days: number
  rank: number
}

export type LeaderboardPeriod = 'day' | 'week' | 'month' | 'year' | 'all'

// Query keys factory
export const pointsKeys = {
  all: ['points'] as const,
  history: (userId: string, limit?: number) => [...pointsKeys.all, 'history', userId, limit] as const,
  leaderboard: (period: LeaderboardPeriod, offset: number) => [...pointsKeys.all, 'leaderboard', period, offset] as const,
  breakdown: (username: string, period: string, offset?: number) => [...pointsKeys.all, 'breakdown', username, period, offset] as const,
}

/**
 * Fetch points history for a user
 */
export function usePointsHistory(userId: string | undefined, limit: number = 10) {
  return useQuery({
    queryKey: pointsKeys.history(userId || '', limit),
    queryFn: async () => {
      if (!userId) return { transactions: [] }
      const data = await apiClient.getPointsHistory(userId, limit)
      return data
    },
    enabled: !!userId,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  })
}

/**
 * Fetch leaderboard with period and offset
 */
export function useLeaderboard(period: LeaderboardPeriod, offset: number = 0) {
  return useQuery({
    queryKey: pointsKeys.leaderboard(period, offset),
    queryFn: async () => {
      const data = await apiClient.getLeaderboard(period, offset)
      return Array.isArray(data) ? data : []
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  })
}

/**
 * Fetch points breakdown for a child
 */
export function usePointsBreakdown(username: string | null, period: string, offset: number = 0) {
  const periodMap: Record<string, string> = {
    'day': 'day',
    'week': 'week',
    'month': 'month',
    'year': 'year',
    'all': 'all-time'
  }

  return useQuery({
    queryKey: pointsKeys.breakdown(username || '', period, offset),
    queryFn: async () => {
      if (!username) return null
      const offsetParam = offset !== 0 ? `&offset=${offset}` : ''
      const response = await apiClient.get(`/api/points/breakdown/${username}?period=${periodMap[period]}${offsetParam}`)
      return response
    },
    enabled: !!username,
    staleTime: 30 * 1000,
  })
}

/**
 * Hook to invalidate all points-related queries
 */
export function useInvalidatePoints() {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({ queryKey: pointsKeys.all })
  }
}
